import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import MemberFormFields, { type MemberDraft } from '../components/MemberFormFields';
import type { MemberWriteResponse, StatusesResponse } from '@shared/api-responses';
import type { MemberStatus } from '@shared/types';

export default function MemberNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'editor' || user?.role === 'admin';

  const [coId, setCoId] = useState('');
  const [draft, setDraft] = useState<MemberDraft>({});
  const [statuses, setStatuses] = useState<MemberStatus[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<StatusesResponse>('/statuses')
      .then((r) => setStatuses(r.statuses))
      .catch(() => {});
  }, []);

  if (!canEdit) {
    return (
      <div className="rounded-lg bg-amber-50 text-amber-800 px-4 py-3 text-sm">
        You need the editor role to add members.
      </div>
    );
  }

  const setField = (name: string, value: string) => setDraft((d) => ({ ...d, [name]: value }));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch<MemberWriteResponse>('/members', {
        method: 'POST',
        body: { ...draft, co_id: coId },
      });
      navigate(`/members/${res.member.co_id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create member');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <Link to="/members" className="text-sm text-dcvfd-accent hover:underline">
          ← Members
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-dcvfd-dark">New Member</h1>
      </div>

      <section className="rounded-lg border border-border-subtle bg-surface-raised p-4 space-y-4">
        {error && <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}

        <div>
          <label className="text-xs uppercase tracking-wide text-zinc-400 mb-1 block" htmlFor="co_id">
            CO ID # <span className="text-red-500">*</span>
          </label>
          <input
            id="co_id"
            inputMode="numeric"
            value={coId}
            onChange={(e) => setCoId(e.target.value)}
            placeholder="Department-assigned Company ID"
            className="w-full md:w-64 rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-dcvfd-accent"
          />
        </div>

        <MemberFormFields draft={draft} onChange={setField} statuses={statuses} />

        <div className="flex gap-2 border-t border-border-subtle pt-3">
          <button
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-dcvfd-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Create member'}
          </button>
          <button
            onClick={() => navigate('/members')}
            disabled={saving}
            className="rounded-lg border border-border-default px-4 py-2 text-sm hover:bg-surface"
          >
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}
